import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import { Team } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Team/fetchTeams/types";
import { fetchTeams } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Team/fetchTeams/fetchTeamsApi";
import {
  CreateTeamRequest,
  CreateTeamResponse,
} from "@/modules/administration/features/EmployeeDirectoryManagement/api/Team/createTeam/types";
import { createTeam } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Team/createTeam/createTeamApi";
import {
  UpdateTeamRequest,
  UpdateTeamResponse,
} from "@/modules/administration/features/EmployeeDirectoryManagement/api/Team/updateTeam/types";
import { updateTeam } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Team/updateTeam/updateTeamApi";
import { deleteTeam } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Team/deleteTeam/deleteTeamApi";

interface TeamState {
  list: Team[];
  loadingList: boolean;
  listError: string | null;

  loadingCreate: boolean;
  createError: string | null;

  loadingUpdate: boolean;
  updateError: string | null;

  loadingDelete: boolean;
  deleteError: string | null;
}

const initialState: TeamState = {
  list: [],
  loadingList: false,
  listError: null,
  loadingCreate: false,
  createError: null,
  loadingUpdate: false,
  updateError: null,
  loadingDelete: false,
  deleteError: null,
};

const teamSlice = createSlice({
  name: "employeeDirectoryTeam",
  initialState,
  reducers: {
    setList(state, action: PayloadAction<Team[]>) {
      state.list = action.payload;
    },
    setLoadingList(state, action: PayloadAction<boolean>) {
      state.loadingList = action.payload;
    },
    setListError(state, action: PayloadAction<string | null>) {
      state.listError = action.payload;
    },
    setLoadingCreate(state, action: PayloadAction<boolean>) {
      state.loadingCreate = action.payload;
    },
    setCreateError(state, action: PayloadAction<string | null>) {
      state.createError = action.payload;
    },
    setLoadingUpdate(state, action: PayloadAction<boolean>) {
      state.loadingUpdate = action.payload;
    },
    setUpdateError(state, action: PayloadAction<string | null>) {
      state.updateError = action.payload;
    },
    setLoadingDelete(state, action: PayloadAction<boolean>) {
      state.loadingDelete = action.payload;
    },
    setDeleteError(state, action: PayloadAction<string | null>) {
      state.deleteError = action.payload;
    },
  },
});

export const {
  setList,
  setLoadingList,
  setListError,
  setLoadingCreate,
  setCreateError,
  setLoadingUpdate,
  setUpdateError,
  setLoadingDelete,
  setDeleteError,
} = teamSlice.actions;

export default teamSlice.reducer;

export const fetchTeamsAction =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch(setLoadingList(true));
    dispatch(setListError(null));
    try {
      const response = await fetchTeams();
      dispatch(setList(response.data));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch teams.";
      dispatch(setListError(message));
    } finally {
      dispatch(setLoadingList(false));
    }
  };

export const createTeamAction =
  (
    requestData: CreateTeamRequest,
    onSuccess?: (created: CreateTeamResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingCreate(true));
    dispatch(setCreateError(null));
    try {
      const response = await createTeam(requestData);
      onSuccess?.(response.data);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create team.";
      dispatch(setCreateError(message));
    } finally {
      dispatch(setLoadingCreate(false));
    }
  };

export const updateTeamAction =
  (
    uuid: string,
    requestData: UpdateTeamRequest,
    onSuccess?: (updated: UpdateTeamResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingUpdate(true));
    dispatch(setUpdateError(null));
    try {
      const response = await updateTeam(uuid, requestData);
      onSuccess?.(response.data);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update team.";
      dispatch(setUpdateError(message));
    } finally {
      dispatch(setLoadingUpdate(false));
    }
  };

export const deleteTeamAction =
  (uuid: string, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingDelete(true));
    dispatch(setDeleteError(null));
    try {
      await deleteTeam(uuid);
      onSuccess?.();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete team.";
      dispatch(setDeleteError(message));
    } finally {
      dispatch(setLoadingDelete(false));
    }
  };
